import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, Calendar, GraduationCap, Calculator, BookOpen, Star,
  MessageCircle, ArrowUpRight, Mail, Settings, X, ChevronDown, Lock, ChevronsLeft,
  ChevronsRight, Target, type LucideIcon,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { NavLink } from '@/features/dashboard/NavLink';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const FB_URL = 'https://www.facebook.com/groups/842778640733978';

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  external?: string;
  locked?: boolean;
}

// ── Expanded nav items ────────────────────────────────────────────────────────

function SidebarNavItem({ item }: { item: NavItem }) {
  if (item.external) {
    return (
      <a
        href={item.external}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-3 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl transition-colors duration-150 group active:scale-[0.98] transition-transform"
      >
        <item.icon className="h-[18px] w-[18px] flex-shrink-0 group-hover:text-gold transition-colors" />
        <span className="font-medium">{item.title}</span>
        <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-50 group-hover:opacity-100 group-hover:text-gold transition-opacity duration-150" />
      </a>
    );
  }

  return (
    <NavLink
      to={item.url}
      end={item.url === '/dashboard'}
      className="flex items-center gap-3 px-4 py-3 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl transition-colors duration-150 group border-l-2 border-transparent hover:border-sidebar-foreground/20 active:scale-[0.98]"
      activeClassName="bg-sidebar-accent text-sidebar-foreground border-l-2 border-gold font-medium [&>svg]:text-gold"
    >
      <item.icon className="h-[18px] w-[18px] flex-shrink-0 group-hover:text-gold transition-colors" />
      <span className="font-medium flex-1">{item.title}</span>
      {item.locked && <Lock className="h-3 w-3 text-sidebar-foreground/25 flex-shrink-0" />}
    </NavLink>
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="px-4 py-3 text-[10px] font-bold tracking-[0.2em] text-sidebar-foreground/40 uppercase">
      {label}
    </p>
  );
}

interface DropdownItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

function NavDropdown({ icon: Icon, label, items }: { icon: LucideIcon; label: string; items: DropdownItem[] }) {
  const { pathname } = useLocation();
  const isAnyActive = items.some(item => pathname.startsWith(item.url));
  const [open, setOpen] = useState(isAnyActive);

  useEffect(() => {
    if (isAnyActive) setOpen(true);
  }, [isAnyActive]);

  return (
    <div>
      <button
        onClick={() => setOpen(prev => !prev)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 text-sm rounded-xl transition-colors duration-150 group border-l-2 active:scale-[0.98]',
          isAnyActive
            ? 'bg-sidebar-accent text-sidebar-foreground border-gold font-medium'
            : 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
        )}
      >
        <Icon className={cn('h-[18px] w-[18px] flex-shrink-0 transition-colors', isAnyActive ? 'text-gold' : 'group-hover:text-gold')} />
        <span className="font-medium flex-1 text-left">{label}</span>
        <ChevronDown className={cn('h-4 w-4 opacity-50 transition-transform duration-200', open && 'rotate-180')} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            style={{ overflow: 'hidden' }}
            className="mt-0.5 ml-4 pl-4 border-l border-sidebar-border space-y-0.5"
          >
            {items.map(item => (
              <NavLink
                key={item.url}
                to={item.url}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors duration-150 group active:scale-[0.98]"
                activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium [&>svg]:text-gold"
              >
                <item.icon className="h-[16px] w-[16px] flex-shrink-0 group-hover:text-gold transition-colors" />
                <span className="font-medium">{item.title}</span>
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Collapsed icon-only nav items ─────────────────────────────────────────────

function CollapsedNavItem({ item }: { item: NavItem }) {
  const { pathname } = useLocation();
  const isActive = item.url === '/dashboard'
    ? pathname === '/dashboard'
    : pathname.startsWith(item.url);

  const iconEl = (
    <item.icon className={cn(
      'h-[18px] w-[18px] transition-colors',
      isActive ? 'text-gold' : 'text-sidebar-foreground/60 group-hover:text-gold',
    )} />
  );

  const lockBadge = item.locked ? (
    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-sidebar border border-sidebar-border flex items-center justify-center pointer-events-none">
      <Lock className="h-2 w-2 text-sidebar-foreground/40" />
    </div>
  ) : null;

  const baseClass = cn(
    'relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 group mx-auto',
    isActive ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent',
  );

  if (item.external) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a href={item.external} target="_blank" rel="noopener noreferrer" className={baseClass}>
            {iconEl}
            {lockBadge}
          </a>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>{item.title}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={item.url}
          end={item.url === '/dashboard'}
          className={baseClass}
          activeClassName="bg-sidebar-accent [&>svg]:text-gold"
        >
          {iconEl}
          {lockBadge}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>{item.title}</TooltipContent>
    </Tooltip>
  );
}

// ── AppSidebar ────────────────────────────────────────────────────────────────

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function AppSidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: AppSidebarProps) {
  const { isAdmin, isMember } = useUserRole();
  const { session, user: authUser } = useAuth();

  const fullName = authUser?.name || session?.user?.email?.split('@')[0] || 'User';
  const initials = fullName
    .split(' ')
    .slice(0, 2)
    .map((n: string) => n[0])
    .join('')
    .toUpperCase();

  const memberHubItems: NavItem[] = [
    { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
    { title: 'Action Tracker', url: '/action-tracker', icon: Target },
    { title: 'Mentors', url: '/mentors', icon: Users },
    { title: 'Calendar', url: '/calendar', icon: Calendar },
  ];

  const toolsDropdownItems: DropdownItem[] = [
    { title: 'Calculators', url: '/calculators', icon: Calculator },
    { title: 'Content Library', url: '/content', icon: GraduationCap },
    { title: 'Resources', url: '/resources', icon: BookOpen },
  ];

  // Flat list used in collapsed icon-only mode (dropdown becomes first child)
  const memberCollapsedItems: NavItem[] = [
    { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
    { title: 'Action Tracker', url: '/action-tracker', icon: Target },
    { title: 'Mentors', url: '/mentors', icon: Users },
    { title: 'Calendar', url: '/calendar', icon: Calendar },
    { title: 'Calculators', url: '/calculators', icon: Calculator },
    { title: 'Content Library', url: '/content', icon: GraduationCap },
    { title: 'Resources', url: '/resources', icon: BookOpen },
    { title: 'Community', url: '/community', icon: MessageCircle, external: FB_URL },
  ];

  const freeTopItems: NavItem[] = [
    { title: 'Mentors', url: '/mentors', icon: Users, locked: true },
    { title: 'Calendar', url: '/calendar', icon: Calendar, locked: true },
    { title: 'Content Library', url: '/content', icon: GraduationCap, locked: true },
    { title: 'Resources', url: '/resources', icon: BookOpen, locked: true },
  ];

  const freeItems: NavItem[] = [
    { title: 'Tools & Calculators', url: '/calculators', icon: Calculator },
    { title: 'Saved Deals', url: '/saved-deals', icon: Star },
  ];

  const freeCollapsedItems: NavItem[] = [
    { title: 'Mentors', url: '/mentors', icon: Users, locked: true },
    { title: 'Calendar', url: '/calendar', icon: Calendar, locked: true },
    { title: 'Content Library', url: '/content', icon: GraduationCap, locked: true },
    { title: 'Resources', url: '/resources', icon: BookOpen, locked: true },
    { title: 'Calculators', url: '/calculators', icon: Calculator },
    { title: 'Saved Deals', url: '/saved-deals', icon: Star },
  ];

  return (
    <aside className={cn(
      'flex flex-col bg-sidebar border-r border-sidebar-border overflow-hidden',
      // Mobile: fixed drawer slides in from left, always full width
      'fixed inset-y-0 left-0 z-50 w-[280px] transition-[transform,width] duration-[280ms] ease-out',
      isOpen ? 'translate-x-0' : '-translate-x-full',
      // Desktop: in-flow, width animates between expanded and collapsed
      'lg:relative lg:inset-auto lg:translate-x-0 lg:flex-shrink-0 lg:h-full lg:z-20',
      isCollapsed ? 'lg:w-[64px]' : 'lg:w-[260px]',
    )}>

      {/* ── Logo + controls ── */}
      <div className={cn(
        'flex items-center flex-shrink-0 border-b border-sidebar-border',
        isCollapsed ? 'lg:flex-col lg:py-5 lg:px-0 lg:gap-3 px-8 pt-10 pb-8 justify-between' : 'px-8 pt-10 pb-8 justify-between',
      )}>
        {/* Logo — hidden when collapsed on desktop */}
        <h1 className={cn(
          'font-serif text-[28px] text-sidebar-foreground font-bold tracking-tight flex items-start leading-none',
          isCollapsed && 'lg:hidden',
        )}>
          First Deal
          <span className="text-[10px] uppercase font-sans tracking-widest text-sidebar-foreground/40 ml-1 mt-1">™</span>
        </h1>

        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden mt-1 p-3 rounded-lg text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          aria-label="Close navigation"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={onToggleCollapse}
          className={cn(
            'hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors flex-shrink-0',
            isCollapsed && 'mt-0',
          )}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed
            ? <ChevronsRight className="w-4 h-4" />
            : <ChevronsLeft className="w-4 h-4" />
          }
        </button>
      </div>

      {/* ── Navigation ── */}
      <nav className={cn(
        'flex-1 overflow-y-auto py-3 space-y-0.5',
        isCollapsed ? 'lg:px-2 px-4' : 'px-4',
      )}>
        {/* Collapsed desktop: icon-only with tooltips — never shown on mobile */}
        <div className={cn('space-y-1', isCollapsed ? 'hidden lg:block' : 'hidden')}>
          {(isMember ? memberCollapsedItems : freeCollapsedItems).map(item => (
            <CollapsedNavItem key={item.url + item.title} item={item} />
          ))}
          {/* Admin icon when collapsed */}
          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/admin"
                  className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-sidebar-accent transition-colors group mx-auto"
                  activeClassName="bg-sidebar-accent [&>svg]:text-gold"
                >
                  <Settings className="h-[18px] w-[18px] text-sidebar-foreground/60 group-hover:text-gold transition-colors" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Admin</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Expanded: full nav with labels (shown on mobile always, desktop only when not collapsed) */}
        <div className={cn(isCollapsed && 'lg:hidden')}>
          {isMember ? (
            <>
              <SectionLabel label="Member Hub" />
              {memberHubItems.map(item => (
                <SidebarNavItem key={item.url + item.title} item={item} />
              ))}
              <NavDropdown icon={BookOpen} label="Tools & Resources" items={toolsDropdownItems} />
              <SidebarNavItem item={{ title: 'Community', url: '/community', icon: MessageCircle, external: FB_URL }} />
            </>
          ) : (
            <>
              {freeTopItems.map(item => (
                <SidebarNavItem key={item.url + item.title} item={item} />
              ))}
              <SectionLabel label="Free Resources" />
              {freeItems.map(item => (
                <SidebarNavItem key={item.url + item.title} item={item} />
              ))}
            </>
          )}
        </div>
      </nav>

      {/* ── Admin / Contact Support (expanded only) ── */}
      <div className={cn('px-4 pb-3', isCollapsed && 'lg:hidden')}>
        {isAdmin ? (
          <NavLink
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl transition-all duration-200 group border-l-2 border-transparent"
            activeClassName="bg-sidebar-accent text-sidebar-foreground border-l-2 border-gold font-medium [&>svg]:text-gold"
          >
            <Settings className="h-[18px] w-[18px] flex-shrink-0 group-hover:text-gold transition-colors" />
            <span className="font-medium">Admin</span>
          </NavLink>
        ) : (
          <a
            href="mailto:support@firstdeal.com"
            className="flex items-center gap-3 px-4 py-3 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl transition-all duration-200 group"
          >
            <Mail className="h-[18px] w-[18px] flex-shrink-0 group-hover:text-gold transition-colors" />
            <span className="font-medium">Contact Support</span>
          </a>
        )}
      </div>

      {/* ── Profile footer ── */}
      {isCollapsed ? (
        /* Collapsed: avatar only with tooltip */
        <div className="hidden lg:flex justify-center py-4 border-t border-sidebar-border flex-shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/profile"
                className="w-10 h-10 rounded-full bg-gold flex items-center justify-center text-sm font-bold text-primary-foreground shadow-md ring-2 ring-gold/20 hover:ring-gold/50 transition-all flex-shrink-0"
              >
                {initials}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              <p className="font-semibold">{fullName}</p>
              <p className="text-xs opacity-75">View Profile</p>
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}

      {/* Expanded profile card (mobile always, desktop only when expanded) */}
      <div className={cn('p-5 border-t border-sidebar-border flex-shrink-0', isCollapsed && 'lg:hidden')}>
        <Link
          to="/profile"
          className="w-full flex items-center gap-3 p-4 rounded-xl bg-sidebar-accent hover:bg-sidebar-accent/80 transition-all duration-200 border border-sidebar-border hover:border-gold/30 group shadow-sm hover:shadow-md"
        >
          <div className="relative flex-shrink-0">
            <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center text-lg font-bold text-primary-foreground shadow-lg ring-2 ring-gold/20 group-hover:ring-gold/40 transition-all">
              {initials}
            </div>
          </div>
          <div className="text-left flex-1 min-w-0">
            <p className="text-sm font-semibold text-sidebar-foreground truncate">{fullName}</p>
            <p className="text-[11px] text-gold truncate mt-0.5">View Profile</p>
          </div>
        </Link>
      </div>
    </aside>
  );
}
